// frontend/src/pages/AssignPoints/HouseSelectPage.js
import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import constants from '../../data/constants.json';

// Build the list from constants.json (source of truth)
const housesFromConstants = (constants?.houses && typeof constants.houses === 'object')
  ? Object.entries(constants.houses)
  : [];

const houses = housesFromConstants.map(([name, color]) => ({
  name,
  color,
  icon: `/icons/${name}.png`,
}));

function HouseSelectPage() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();

  const handleSelect = (houseName) => {
    // Keep your original behavior
    localStorage.setItem('selectedHouse', houseName);

    // Also pass it in the URL for downstream pages that prefer query params
    const next = new URLSearchParams(sp);
    next.set('house_name', houseName);
    next.set('flow', 'house'); // non-breaking; can help downstream branching

    // ✅ Route to the existing ReasonSelectPage path
    navigate(`/assign/reasons?${next.toString()}`);
  };

  const onKeyActivate = (e, houseName) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSelect(houseName);
    }
  };

  return (
    <div style={s.page}>
      <div style={s.headerRow}>
        <h1 style={s.h1}>Select a House</h1>
        <button type="button" onClick={() => navigate(-1)} style={s.backBtn} aria-label="Go back">
          ← Back
        </button>
      </div>

      {houses.length === 0 ? (
        <div style={s.emptyNote}>
          No houses configured. Add them in <code>data/constants.json</code> under <code>houses</code>.
        </div>
      ) : (
        <>
          {/* First row (two houses) */}
          <div style={s.row}>
            {houses.slice(0, 2).map((house) => (
              <button
                key={house.name}
                onClick={() => handleSelect(house.name)}
                onKeyDown={(e) => onKeyActivate(e, house.name)}
                style={{ ...s.card, backgroundColor: house.color || '#334155' }}
                aria-label={`Select ${house.name}`}
              >
                <img
                  src={house.icon}
                  alt={`${house.name} crest`}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  style={s.icon}
                />
                {house.name}
              </button>
            ))}
          </div>

          {/* Second row (remaining houses) */}
          <div style={s.row}>
            {houses.slice(2).map((house) => (
              <button
                key={house.name}
                onClick={() => handleSelect(house.name)}
                onKeyDown={(e) => onKeyActivate(e, house.name)}
                style={{ ...s.card, backgroundColor: house.color || '#334155' }}
                aria-label={`Select ${house.name}`}
              >
                <img
                  src={house.icon}
                  alt={`${house.name} crest`}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  style={s.icon}
                />
                {house.name}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const s = {
  page: { padding: 16 },
  headerRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  h1: { margin: 0, color: '#003e73' },
  backBtn: {
    border: '1px solid rgba(0,0,0,0.15)',
    background: '#fff',
    color: '#333',
    borderRadius: 8,
    height: 36,
    padding: '0 12px',
    cursor: 'pointer',
  },
  row: {
    display: 'flex',
    justifyContent: 'center',
    gap: '30px',
    marginTop: '30px',
    marginBottom: '30px',
    flexWrap: 'wrap',
  },
  card: {
    color: 'white',
    padding: '20px',
    border: 'none',
    borderRadius: '14px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '200px',
    height: '220px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    fontWeight: 'bold',
    fontSize: '18px',
    transition: 'transform 0.2s ease',
  },
  icon: { width: '64px', height: '64px', marginBottom: '15px' },
  emptyNote: {
    marginTop: 16,
    background: '#FFF8E1',
    border: '1px solid #FFE082',
    color: '#6A4F00',
    borderRadius: 8,
    padding: '10px 12px',
  },
};

export default HouseSelectPage;
